import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  WORDS,
  UNITS,
  PARTICLES,
  ALPHABET,
  ALPHABET_NOTE,
  PARTICLE_NOTE,
  buildLesson,
  buildReview,
  buildStretch,
  stretchWords,
  lessonWords,
  findExample,
  filterGlossary,
  glossarySections,
  patternsIntroducedAt,
  shuffle,
  unlockedWords,
  unlockedPhrases,
  gameUnlockPoint,
  wordsThroughLesson,
} from "../lib/tokiPonaData";
import { Icon, Dots, Html, SpeakButton } from "../components/tokiPonaUi";
import { TapWord, WordPopover, wordKey } from "../components/TokiPonaTapWord";
import { speakTokiPona, speechAvailable } from "../lib/tokiPonaSpeech";
import {
  GAMES,
  SpeedQuiz,
  MemoryMatch,
  SentenceBuilder,
  Flashcards,
} from "../components/TokiPonaGames";
import ComposeLab from "../components/TokiPonaCompose";
import TokiPonaRoles from "../components/TokiPonaRoles";
import TokiPonaSprint from "../components/TokiPonaSprint";
import TokiPonaSync from "../components/TokiPonaSync";
import "../styles/tokipona.css";

/* ============================================================
   o kama sona — a toki pona trainer (React port)
   Save state is per-user: an anonymous id is stored on this
   device and progress (xp, streak, finished lessons) is synced
   to Firestore at tokipona/{userId}. Falls back to localStorage
   when Firebase isn't configured.
   ============================================================ */

const USER_KEY = "tokiPonaUserId";
const LOCAL_PROGRESS_KEY = "tokiPonaProgress"; // offline fallback

// Role roulette and the hat sprint open once units 1–2 are done (16 words):
// both modes lean on noun/verb/modifier, and modifiers aren't taught
// until u2-1.
const ROULETTE_MIN_WORDS = 16;

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getUserId = () => {
  let id = localStorage.getItem(USER_KEY);
  if (!id) {
    id = newId();
    localStorage.setItem(USER_KEY, id);
  }
  return id;
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const TokiPona = () => {
  // ---- persisted progress (synced per-user) --------------------------------
  const [userId, setUserId] = useState(getUserId);
  const [progress, setProgress] = useState({
    xp: 0,
    streak: 0,
    completed: {},
    name: "",
    lastDate: "",
    lastReviewDate: "",
  });
  const [loaded, setLoaded] = useState(false);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  const docRef = useMemo(
    () => (db ? doc(db, "tokipona", userId) : null),
    [userId]
  );

  // Live sync from Firestore (or load once from localStorage offline).
  useEffect(() => {
    if (!docRef) {
      try {
        const saved = localStorage.getItem(LOCAL_PROGRESS_KEY);
        if (saved) setProgress((p) => ({ ...p, ...JSON.parse(saved) }));
      } catch {
        /* ignore */
      }
      setLoaded(true);
      return undefined;
    }
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setProgress({
          xp: d.xp || 0,
          streak: d.streak || 0,
          completed: d.completed || {},
          name: d.name || "",
          lastDate: d.lastDate || "",
          lastReviewDate: d.lastReviewDate || "",
        });
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return unsub;
  }, [docRef]);

  // Merge a patch into progress and persist it (Firestore + local mirror).
  const persist = useCallback(
    (patch) => {
      const next = { ...progressRef.current, ...patch };
      setProgress(next);
      try {
        localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private-mode */
      }
      if (docRef) {
        setDoc(docRef, { ...patch, updatedAt: Date.now() }, { merge: true }).catch(
          () => {}
        );
      }
    },
    [docRef]
  );

  // Award XP from a practice game (no streak / lesson-completion side effects).
  const awardXp = useCallback(
    (n) => {
      if (!n) return;
      persist({ xp: (progressRef.current.xp || 0) + n });
    },
    [persist]
  );

  // ---- transient lesson runtime -------------------------------------------
  const [screen, setScreen] = useState("home"); // home | alphabet | words | teach | lesson | done | games | game | sync | compose | study | roles | sprint

  // Re-bind this device to a code from another device (that learner's user id).
  // docRef is memoized off userId, so setting it re-subscribes the snapshot
  // listener to that account and its progress loads in. Show the loading state
  // until the first snapshot of the new doc arrives.
  const carryProgress = useCallback((code) => {
    try {
      localStorage.setItem(USER_KEY, code);
    } catch {
      /* ignore quota / private-mode */
    }
    setLoaded(false);
    setUserId(code);
    setScreen("home");
  }, []);
  const [activeGame, setActiveGame] = useState(null);
  const [glossaryQuery, setGlossaryQuery] = useState(""); // glossary search
  const [glossaryOpen, setGlossaryOpen] = useState(null); // expanded glossary word
  // The page scrolls as one document, so the home screen's scroll offset
  // would otherwise carry into the glossary — and its entry card sits at the
  // very bottom of home, meaning you'd always arrive mid-list. Start at the
  // top instead.
  useEffect(() => {
    if (screen === "words") window.scrollTo(0, 0);
  }, [screen]);
  const [lesson, setLesson] = useState(null); // {unit, idx, id, focus, questions}
  const [teachIdx, setTeachIdx] = useState(0); // carousel position in teach phase
  const [qi, setQi] = useState(0);
  const [hearts, setHearts] = useState(5);
  const [correct, setCorrect] = useState(0);
  const [total, setTotal] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [lastCorrect, setLastCorrect] = useState(false);
  const [selection, setSelection] = useState(null);
  const [matchState, setMatchState] = useState(null);
  const [buildState, setBuildState] = useState(null);
  const [doneInfo, setDoneInfo] = useState(null);
  const [tapPopover, setTapPopover] = useState(null); // { word, left, top }
  const [panelOpen, setPanelOpen] = useState(false); // "learned so far" slide-over
  const appRef = useRef(null);

  // Tap-a-word popover: only one open at a time, closed on question change.
  useEffect(() => {
    setTapPopover(null);
  }, [qi]);

  const openTapWord = (word, rect) => {
    const appEl = appRef.current;
    if (!appEl) return;
    const appRect = appEl.getBoundingClientRect();
    const POP_W = 250;
    const left = Math.min(
      Math.max(rect.left - appRect.left, 8),
      appRect.width - POP_W - 8
    );
    const top = rect.bottom - appRect.top + 6;
    setTapPopover((cur) =>
      cur && cur.word === word && cur.top === top && cur.left === left
        ? null
        : { word, left, top }
    );
  };

  // Listening questions announce themselves: speak the prompt when one
  // arrives (and again on demand via the replay button).
  useEffect(() => {
    if (screen !== "lesson" || !lesson) return;
    const q = lesson.questions[qi];
    if (q && !q.noAudio && (q.type === "listen" || q.type === "listenPhrase")) {
      speakTokiPona(q.type === "listen" ? q.word : q.tp);
    }
  }, [screen, lesson, qi]);

  const startLesson = (unit, idx) => {
    const id = `${unit.id}-${idx}`;
    setLesson({
      unit,
      idx,
      id,
      focus: lessonWords(unit, idx),
      grammar: patternsIntroducedAt(id),
      questions: buildLesson(unit, idx, {
        audio: speechAvailable && !progressRef.current.soundOff,
      }),
    });
    setQi(0);
    setHearts(5);
    setCorrect(0);
    setTotal(0);
    setAnswered(false);
    setLastCorrect(false);
    setSelection(null);
    setMatchState(null);
    setBuildState(null);
    setTeachIdx(0);
    setTapPopover(null);
    setPanelOpen(false);
    // Meet the new words first, then practice them.
    setScreen("teach");
  };

  // The end-of-unit "word stretch": same quiz loop, no teach carousel —
  // every question here re-meets known words in their other senses.
  const startStretch = (unit) => {
    setLesson({
      unit,
      idx: null,
      id: `${unit.id}-x`,
      stretch: true,
      focus: stretchWords(unit.id),
      grammar: [],
      questions: buildStretch(unit.id),
    });
    setQi(0);
    setHearts(5);
    setCorrect(0);
    setTotal(0);
    setAnswered(false);
    setLastCorrect(false);
    setSelection(null);
    setMatchState(null);
    setBuildState(null);
    setTeachIdx(0);
    setTapPopover(null);
    setPanelOpen(false);
    setScreen("lesson");
  };

  // Jump from the teach carousel into the quiz.
  const beginQuiz = () => setScreen("lesson");

  const goHome = () => setScreen("home");

  // "o wawa sin" — a short mixed-review quiz from everything already learned.
  // Skips the teach carousel entirely and drops straight into the quiz loop,
  // reusing the same lesson state shape (with `review: true` so finishLesson
  // and the done screen can branch).
  const startReview = () => {
    const questions = buildReview(progressRef.current.completed, {
      audio: speechAvailable && !progressRef.current.soundOff,
    });
    if (!questions.length) return; // nothing learned yet — card shouldn't be visible
    setLesson({
      id: "review",
      review: true,
      unit: null,
      idx: null,
      focus: [],
      grammar: [],
      questions,
    });
    setQi(0);
    setHearts(5);
    setCorrect(0);
    setTotal(0);
    setAnswered(false);
    setLastCorrect(false);
    setSelection(null);
    setMatchState(null);
    setBuildState(null);
    setTeachIdx(0);
    setTapPopover(null);
    setPanelOpen(false);
    setScreen("lesson"); // straight to the quiz — no teach carousel for a review
  };

  const finishLesson = (passed, runtime) => {
    // runtime carries the up-to-date tallies so we don't read stale state.
    const h = runtime?.hearts ?? hearts;
    const c = runtime?.correct ?? correct;
    const t = runtime?.total ?? total;
    const isReview = !!lesson.review;
    const isStretch = !!lesson.stretch;
    if (passed) {
      const perfect = h === 5;
      const award = isReview ? 6 + (perfect ? 4 : 0) : 12 + (perfect ? 8 : 0);
      const acc = t ? Math.round((c / t) * 100) : 100;
      // Light streak: bump when the first lesson (or review) of a new day is
      // finished.
      const today = todayKey();
      let streak = progressRef.current.streak || 0;
      if (progressRef.current.lastDate !== today) streak += 1;
      const patch = {
        xp: (progressRef.current.xp || 0) + award,
        streak,
        lastDate: today,
      };
      if (isReview) {
        // Reviews never write into `completed` — that map only ever holds
        // lesson ids, unit-stretch ids (`u3-x`), and "letters".
        patch.lastReviewDate = today;
      } else {
        patch.completed = { ...progressRef.current.completed, [lesson.id]: true };
      }
      persist(patch);
      setDoneInfo({
        passed: true,
        award,
        acc,
        hearts: h,
        review: isReview,
        stretch: isStretch,
      });
    } else {
      setDoneInfo({
        passed: false,
        correct: c,
        total: t,
        review: isReview,
        stretch: isStretch,
      });
    }
    setScreen("done");
  };

  const grade = (isCorrect) => {
    const nextTotal = total + 1;
    const nextCorrect = correct + (isCorrect ? 1 : 0);
    const nextHearts = hearts - (isCorrect ? 0 : 1);
    setAnswered(true);
    setLastCorrect(isCorrect);
    setTotal(nextTotal);
    setCorrect(nextCorrect);
    setHearts(nextHearts);
    if (nextHearts <= 0) {
      setTimeout(
        () =>
          finishLesson(false, {
            hearts: nextHearts,
            correct: nextCorrect,
            total: nextTotal,
          }),
        450
      );
    }
  };

  const advance = () => {
    const nextQi = qi + 1;
    setAnswered(false);
    setSelection(null);
    setMatchState(null);
    setBuildState(null);
    if (nextQi >= lesson.questions.length) {
      finishLesson(true);
      return;
    }
    setQi(nextQi);
  };

  // ---- matching interactions ----------------------------------------------
  const ensureMatch = (q) => {
    if (matchState) return matchState;
    const ms = {
      left: shuffle(q.pairs.map((p) => p.tp)),
      right: shuffle(q.pairs.map((p) => p.en)),
      pairs: q.pairs,
      sel: null,
      done: [],
      miss: [],
      wrongCount: 0,
    };
    setMatchState(ms);
    return ms;
  };

  const matchTap = (k) => {
    const ms = matchState;
    if (!ms) return;
    const tp = k.startsWith("L:") ? k.slice(2) : null;
    if (tp && ms.done.includes(tp)) return;

    if (!ms.sel) {
      setMatchState({ ...ms, sel: k, miss: [] });
      return;
    }
    if (ms.sel === k) {
      setMatchState({ ...ms, sel: null });
      return;
    }
    const a = ms.sel;
    const b = k;
    if (a[0] === b[0]) {
      // same side — move the selection
      setMatchState({ ...ms, sel: k });
      return;
    }
    const lKey = a.startsWith("L:") ? a : b;
    const rKey = a.startsWith("R:") ? a : b;
    const tpv = lKey.slice(2);
    const env = rKey.slice(2);
    const isMatch = ms.pairs.find((p) => p.tp === tpv).en === env;
    if (isMatch) {
      setMatchState({ ...ms, sel: null, done: [...ms.done, tpv], miss: [] });
    } else {
      const nextHearts = hearts - 1;
      setHearts(nextHearts);
      setMatchState({ ...ms, sel: null, wrongCount: ms.wrongCount + 1, miss: [a, b] });
      setTimeout(() => {
        setMatchState((cur) => (cur ? { ...cur, miss: [] } : cur));
      }, 320);
      if (nextHearts <= 0) {
        setTimeout(
          () => finishLesson(false, { hearts: nextHearts, correct, total }),
          450
        );
      }
    }
  };

  const finishMatch = () => {
    const ms = matchState;
    const credited = ms && ms.wrongCount === 0 ? 1 : 0;
    setTotal((t) => t + 1);
    setCorrect((c) => c + credited);
    advance();
  };

  // ---- build-the-sentence interactions ------------------------------------
  const ensureBuild = (q) => {
    if (buildState) return buildState;
    const bs = {
      bank: shuffle(q.tiles).map((tok, i) => ({ id: i, tok })),
      answer: [],
    };
    setBuildState(bs);
    return bs;
  };
  const buildToAnswer = (item) => {
    if (answered) return;
    setBuildState((b) => ({
      bank: b.bank.filter((x) => x.id !== item.id),
      answer: [...b.answer, item],
    }));
  };
  const buildToBank = (item) => {
    if (answered) return;
    setBuildState((b) => ({
      answer: b.answer.filter((x) => x.id !== item.id),
      bank: [...b.bank, item],
    }));
  };

  // ---- derived: unlocked vocabulary for the practice games ----------------
  const unlockedVocab = useMemo(
    () =>
      unlockedWords(progress.completed).map((k) => ({
        key: k,
        g: WORDS[k].g,
        n: WORDS[k].n,
      })),
    [progress.completed]
  );
  const rouletteReady = unlockedVocab.length >= ROULETTE_MIN_WORDS;
  const rouletteUnlockAt = rouletteReady
    ? null
    : gameUnlockPoint({ min: ROULETTE_MIN_WORDS });
  const gamePhrases = useMemo(
    () => unlockedPhrases(unlockedWords(progress.completed)),
    [progress.completed]
  );

  // ---- derived: which lesson is "current" ---------------------------------
  const homeUnits = useMemo(() => {
    const completed = progress.completed || {};
    let firstIncompleteFound = false;
    return UNITS.map((u, ui) => {
      const nodes = [];
      for (let i = 0; i < u.lessons; i++) {
        const id = `${u.id}-${i}`;
        const done = !!completed[id];
        const prevId =
          i > 0
            ? `${u.id}-${i - 1}`
            : ui > 0
            ? `${UNITS[ui - 1].id}-${UNITS[ui - 1].lessons - 1}`
            : null;
        const unlocked = done || prevId === null || !!completed[prevId];
        let cls = "node";
        let icon = "book";
        if (done) {
          cls += " done";
          icon = "check";
        } else if (unlocked && !firstIncompleteFound) {
          cls += " current pulse";
          firstIncompleteFound = true;
        } else if (unlocked) {
          cls += " current";
        } else {
          cls += " locked";
          icon = "lock";
        }
        nodes.push({
          id,
          cls,
          icon,
          unlocked,
          label: done
            ? "reviewed"
            : unlocked
            ? i === 0
              ? "start"
              : `lesson ${i + 1}`
            : "locked",
          ui,
          i,
        });
      }
      // The unit's capstone: a "word stretch" over the same words' other
      // senses. Optional — it never gates the next unit and never claims
      // the pulse — but it sits in the path so it's part of the journey.
      const sid = `${u.id}-x`;
      const lessonsDone = Array.from(
        { length: u.lessons },
        (_, i) => !!completed[`${u.id}-${i}`]
      ).every(Boolean);
      const sDone = !!completed[sid];
      const sUnlocked = sDone || lessonsDone;
      nodes.push({
        id: sid,
        cls: `node stretch ${sDone ? "done" : sUnlocked ? "current" : "locked"}`,
        icon: sDone ? "check" : sUnlocked ? "bolt" : "lock",
        unlocked: sUnlocked,
        stretch: true,
        label: sDone ? "stretched" : sUnlocked ? "word stretch" : "locked",
        ui,
        i: -1,
      });
      return { unit: u, ui, nodes };
    });
  }, [progress.completed]);

  // ---- derived: daily warm-up card visibility/state ------------------------
  const hasCompletedLesson = Object.keys(progress.completed || {}).some(
    (k) => k !== "letters" && progress.completed[k]
  );
  const reviewedToday = progress.lastReviewDate === todayKey();

  // ---- render: shells ------------------------------------------------------
  if (!loaded) {
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="done-screen">
            <Dots />
            <div className="sub" style={{ marginTop: 16 }}>
              o awen lili… (loading your progress)
            </div>
          </div>
        </div>
      </div>
    );
  }

  const topbar = (
    <div className="topbar">
      <div className="brandmark">
        <b>o kama sona</b>
        <small>
          {progress.name ? `toki, ${progress.name}` : "learn toki pona"}
        </small>
      </div>
      <div className="stat streak">
        <Icon name="flame" />
        {progress.streak}
      </div>
      <div className="stat xp">
        <Icon name="bolt" />
        {progress.xp}
      </div>
      {speechAvailable && (
        <button
          className={`iconbtn sound-toggle ${progress.soundOff ? "off" : ""}`}
          onClick={() => persist({ soundOff: !progress.soundOff })}
          aria-label={
            progress.soundOff
              ? "sound is off — lessons skip listening questions; tap to turn on"
              : "sound is on — tap to mute and skip listening questions"
          }
        >
          <Icon name={progress.soundOff ? "speakerOff" : "speaker"} />
        </button>
      )}
    </div>
  );

  // ---- render: home --------------------------------------------------------
  if (screen === "home") {
    return (
      <div className="tp-root">
        <div className="tp-app">
          {topbar}
          <div className="scroll">
            <div className="hero">
              <h1>
                toki pona li pona.
                <br />o kama sona e ona.
              </h1>
              <p>
                The little language of good. ~130 words, no clutter. Each lesson
                opens by introducing its new words, then drills them — and after
                every answer you can revisit what the word really <i>means</i>.
                Your progress saves automatically.
              </p>
              <NameBar name={progress.name} onSave={(name) => persist({ name })} />
            </div>

            <button
              className="compose-entry"
              onClick={() => setScreen("compose")}
            >
              <div className="ic">
                <Icon name="layers" />
              </div>
              <div className="txt">
                <div className="ttl">nasin toki · the sentence lab</div>
                <div className="sub">
                  {unlockedVocab.length
                    ? `build your own sentences with your ${unlockedVocab.length} words`
                    : "finish a lesson, then build your own sentences"}
                </div>
              </div>
              <span className="chev">›</span>
            </button>

            <button className="study-entry" onClick={() => setScreen("study")}>
              <div className="ic">
                <Icon name="shuffle" />
              </div>
              <div className="txt">
                <div className="ttl">sona nimi · word study</div>
                <div className="sub">
                  role roulette, hat sprint & the word garden — go deep on
                  single words
                </div>
              </div>
              <span className="chev">›</span>
            </button>

            <button
              className={`letters-entry ${
                progress.completed.letters ? "done" : ""
              }`}
              onClick={() => setScreen("alphabet")}
            >
              <div className="ic">a</div>
              <div className="txt">
                <div className="ttl">nimi sitelen · the sounds</div>
                <div className="sub">
                  {progress.completed.letters
                    ? "the 14 letters — revisit any time"
                    : "start here · 14 letters, all phonetic"}
                </div>
              </div>
              <span className="chev">
                {progress.completed.letters ? "✓" : "›"}
              </span>
            </button>

            {hasCompletedLesson && (
              <button
                className={`review-entry ${reviewedToday ? "done" : ""}`}
                onClick={startReview}
              >
                <div className="ic">
                  <Icon name="flame" />
                </div>
                <div className="txt">
                  <div className="ttl">o wawa sin</div>
                  <div className="sub">
                    {reviewedToday
                      ? "done today ✓ — tap for another round"
                      : "daily warm-up — a quick mix from everything you've learned"}
                  </div>
                </div>
                <span className="chev">{reviewedToday ? "✓" : "›"}</span>
              </button>
            )}

            {homeUnits.map(({ unit, ui, nodes }) => (
              <div className="unit" key={unit.id}>
                <div className="unit-head">
                  <div>
                    <div className="ttl">{unit.title}</div>
                    <div className="sub">unit {ui + 1}</div>
                  </div>
                  <div className="gloss">{unit.gloss}</div>
                </div>
                <div className="nodes">
                  {nodes.map((n, idx) => (
                    <React.Fragment key={n.id}>
                      <div className="node-wrap">
                        <button
                          className={n.cls}
                          disabled={!n.unlocked}
                          onClick={() =>
                            n.unlocked &&
                            (n.stretch
                              ? startStretch(UNITS[n.ui])
                              : startLesson(UNITS[n.ui], n.i))
                          }
                        >
                          <Icon name={n.icon} />
                        </button>
                        <div className="node-label">{n.label}</div>
                      </div>
                      {idx < nodes.length - 1 && <div className="connector" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            <button className="games-entry" onClick={() => setScreen("games")}>
              <div className="ic">
                <Icon name="cards" />
              </div>
              <div className="txt">
                <div className="ttl">o musi — practice games</div>
                <div className="sub">
                  {unlockedVocab.length
                    ? `play with your ${unlockedVocab.length} unlocked words`
                    : "finish a lesson to unlock games"}
                </div>
              </div>
              <span className="chev">›</span>
            </button>

            <button className="glossary-entry" onClick={() => setScreen("words")}>
              <div className="ic">
                <Icon name="book" />
              </div>
              <div className="txt">
                <div className="ttl">nimi ale · the glossary</div>
                <div className="sub">
                  every word in one place — meanings, examples, audio
                </div>
              </div>
              <span className="chev">›</span>
            </button>

            <button
              className="glossary-entry sync-entry"
              onClick={() => setScreen("sync")}
            >
              <div className="ic">
                <Icon name="shuffle" />
              </div>
              <div className="txt">
                <div className="ttl">o tawa e sona · carry your progress</div>
                <div className="sub">
                  move your xp, streak and unlocks to another device
                </div>
              </div>
              <span className="chev">›</span>
            </button>

            <div className="footnote">
              <Dots />
              <br />
              {Object.keys(WORDS).length} words loaded
            </div>
          </div>
          <button
            className="games-fab"
            onClick={() => setScreen("games")}
            aria-label="practice games"
          >
            <Icon name="cards" />
            <span>games</span>
          </button>
        </div>
      </div>
    );
  }

  // ---- render: carry progress (cross-device) -------------------------------
  if (screen === "sync") {
    return (
      <TokiPonaSync
        code={userId}
        isCloud={!!docRef}
        onBack={goHome}
        onCarry={carryProgress}
      />
    );
  }

  // ---- render: sentence lab (free composition sandbox) ---------------------
  if (screen === "compose") {
    return (
      <div className="tp-root">
        <div className="tp-app compose-app">
          <div className="subbar">
            <button className="iconbtn" onClick={goHome} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">nasin toki · sentence lab</div>
            <div className="stat xp">
              <Icon name="bolt" />
              {progress.xp}
            </div>
          </div>
          <div className="scroll">
            <ComposeLab completed={progress.completed} />
          </div>
        </div>
      </div>
    );
  }

  // ---- render: word-study hub (roulette, sprint, garden) -------------------
  if (screen === "study") {
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={goHome} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">sona nimi · word study</div>
            <div className="stat xp">
              <Icon name="bolt" />
              {progress.xp}
            </div>
          </div>
          <div className="scroll">
            <p className="games-intro">
              Three ways to go deep on single words: spot the hat a word is
              wearing, dress it in every hat yourself, or sit with it awhile
              in the garden.
            </p>
            <button
              className={`roles-entry ${rouletteReady ? "" : "locked"}`}
              disabled={!rouletteReady}
              onClick={() => rouletteReady && setScreen("roles")}
            >
              <div className="ic">
                <Icon name={rouletteReady ? "shuffle" : "lock"} />
              </div>
              <div className="txt">
                <div className="ttl">len nimi · role roulette</div>
                <div className="sub">
                  {rouletteReady
                    ? "one word, many hats — noun, verb, modifier & more"
                    : rouletteUnlockAt
                    ? `unlocks · unit ${rouletteUnlockAt.unitNum} · lesson ${rouletteUnlockAt.lessonNum} — learn how modifiers work first`
                    : "locked"}
                </div>
              </div>
              <span className="chev">{rouletteReady ? "›" : ""}</span>
            </button>

            <button
              className={`sprint-entry ${rouletteReady ? "" : "locked"}`}
              disabled={!rouletteReady}
              onClick={() => rouletteReady && setScreen("sprint")}
            >
              <div className="ic">
                <Icon name={rouletteReady ? "flame" : "lock"} />
              </div>
              <div className="txt">
                <div className="ttl">len mute · hat sprint</div>
                <div className="sub">
                  {rouletteReady
                    ? "dress one word in every hat — you build the sentences"
                    : rouletteUnlockAt
                    ? `unlocks · unit ${rouletteUnlockAt.unitNum} · lesson ${rouletteUnlockAt.lessonNum} — with the roulette`
                    : "locked"}
                </div>
              </div>
              <span className="chev">{rouletteReady ? "›" : ""}</span>
            </button>

            <Link className="garden-entry" to="/kon-nimi">
              <div className="ic">
                <Icon name="book" />
              </div>
              <div className="txt">
                <div className="ttl">kon nimi · the word garden</div>
                <div className="sub">
                  a slow journal — every word is a plant, and your writing
                  makes it grow
                </div>
              </div>
              <span className="chev">›</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- render: role roulette (one word, many hats) -------------------------
  if (screen === "roles") {
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={() => setScreen("study")} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">len nimi · role roulette</div>
            <div className="stat xp">
              <Icon name="bolt" />
              {progress.xp}
            </div>
          </div>
          <div className="scroll">
            <TokiPonaRoles completed={progress.completed} onAward={awardXp} />
          </div>
        </div>
      </div>
    );
  }

  // ---- render: hat sprint (build the sentence yourself) --------------------
  if (screen === "sprint") {
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={() => setScreen("study")} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">len mute · hat sprint</div>
            <div className="stat xp">
              <Icon name="bolt" />
              {progress.xp}
            </div>
          </div>
          <div className="scroll">
            <TokiPonaSprint completed={progress.completed} onAward={awardXp} />
          </div>
        </div>
      </div>
    );
  }

  // ---- render: games hub ---------------------------------------------------
  if (screen === "games") {
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={goHome} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">o musi · games</div>
            <div className="stat xp">
              <Icon name="bolt" />
              {progress.xp}
            </div>
          </div>
          <div className="scroll">
            <p className="games-intro">
              A side-room for practice. These play with the words you've already
              unlocked in lessons — no hearts, just for fun (and a little XP).
            </p>
            <div className="game-cards">
              {GAMES.map((g) => {
                const available = g.phrases
                  ? gamePhrases.length > 0
                  : unlockedVocab.length >= (g.min || 1);
                const unlockAt = available ? null : gameUnlockPoint(g);
                const lockMsg = g.phrases
                  ? "unlock enough words to form sentences"
                  : `unlock ${g.min} words first`;
                return (
                  <button
                    key={g.key}
                    className={`game-card ${available ? "" : "locked"}`}
                    disabled={!available}
                    onClick={() => {
                      if (!available) return;
                      setActiveGame(g.key);
                      setScreen("game");
                    }}
                  >
                    <div className="ic">
                      <Icon name={available ? g.icon : "lock"} />
                    </div>
                    <div className="txt">
                      <div className="ttl">{g.title}</div>
                      <div className="kind">{g.sub}</div>
                      <div className="desc">{available ? g.desc : lockMsg}</div>
                      {!available && (
                        <div className="lock-badge">
                          <Icon name="lock" />
                          {unlockAt
                            ? `unlocks · unit ${unlockAt.unitNum} · lesson ${unlockAt.lessonNum}`
                            : "locked"}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="footnote">
              <Dots />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- render: alphabet primer --------------------------------------------
  // A tap-to-hear reference for toki pona's fourteen letters, meant to be met
  // before the first lesson. Tapping a letter speaks it (a vowel on its own, a
  // consonant via its example word, since a bare consonant has no sound).
  if (screen === "alphabet") {
    const markLearned = () => {
      if (!progressRef.current.completed.letters) {
        persist({
          completed: { ...progressRef.current.completed, letters: true },
        });
      }
      goHome();
    };
    // Plain function, called (not rendered as <LetterCard/>), so it stays one
    // shared bit of markup without becoming an unstable nested component.
    const letterCard = (item, hear) => (
      <div className="alpha-card" key={item.l}>
        <button
          type="button"
          className="alpha-letter"
          onClick={() => speakTokiPona(hear)}
          aria-label={`hear the letter ${item.l}`}
        >
          {item.l}
        </button>
        <div className="alpha-body">
          <div className="alpha-say">{item.say}</div>
          {item.note && <Html className="notes" html={item.note} />}
          <div className="alpha-ex">
            <span className="tp">{item.ex}</span>
            <SpeakButton text={item.ex} />
            <span className="en">{item.exEn}</span>
          </div>
        </div>
      </div>
    );
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={goHome} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">nimi sitelen · the sounds</div>
          </div>
          <div className="scroll">
            <p className="games-intro">
              Toki pona has just fourteen letters, and each one always makes the
              same sound. Tap a letter to hear it.
            </p>
            <Html className="notes alpha-note" html={ALPHABET_NOTE} />
            <div className="alpha-group-label">five vowels</div>
            <div className="alpha-list">
              {ALPHABET.vowels.map((v) => letterCard(v, v.l))}
            </div>
            <div className="alpha-group-label">nine consonants</div>
            <div className="alpha-list">
              {ALPHABET.consonants.map((c) => letterCard(c, c.ex))}
            </div>
            <button
              className="btn"
              style={{ marginTop: 20 }}
              onClick={markLearned}
            >
              o kama sona e nimi — start learning
            </button>
            <div className="footnote">
              <Dots />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- render: glossary (nimi ale) -----------------------------------------
  // A reference page over the whole vocabulary: every word grouped by unit
  // (plus the particles), searchable, each entry expandable into its full
  // note and an example phrase — with audio throughout.
  if (screen === "words") {
    const sections = filterGlossary(glossarySections(), glossaryQuery);
    const unlockedSet = new Set(unlockedVocab.map((w) => w.key));
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={goHome} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">nimi ale · the glossary</div>
          </div>
          <div className="scroll">
            <p className="games-intro">
              Every word in the course, in one place. Tap a word to read what
              it really means, and the speaker to hear it. Words you've met in
              lessons are marked ✓.
            </p>
            <input
              className="gloss-search"
              type="search"
              placeholder="o alasa e nimi — search words & meanings"
              aria-label="search the glossary"
              value={glossaryQuery}
              onChange={(e) => setGlossaryQuery(e.target.value)}
            />
            {sections.length === 0 && (
              <p className="gloss-empty">
                nimi ala li kama. — nothing matches “{glossaryQuery.trim()}”.
              </p>
            )}
            {sections.map((s) => (
              <React.Fragment key={s.id}>
                <div className="gloss-section">
                  <span className="ttl">{s.title}</span>
                  <span className="sub">
                    {s.label} · {s.gloss}
                  </span>
                </div>
                <div className="gloss-list">
                  {s.entries.map((e) => {
                    const open = glossaryOpen === e.word;
                    return (
                      <div
                        className={`gloss-entry ${open ? "open" : ""}`}
                        key={e.word}
                      >
                        <button
                          type="button"
                          className="gloss-row"
                          aria-expanded={open}
                          onClick={() =>
                            setGlossaryOpen(open ? null : e.word)
                          }
                        >
                          <span className="tp">{e.word}</span>
                          <SpeakButton text={e.word} />
                          <span className="g">{e.gloss}</span>
                          {unlockedSet.has(e.word) && (
                            <span className="known" title="met in a lesson">
                              ✓
                            </span>
                          )}
                        </button>
                        {open && (
                          <div className="gloss-body">
                            <Html className="notes" html={e.note} />
                            {e.example && (
                              <div className="gloss-ex">
                                <span className="tp">{e.example.tp}</span>
                                <SpeakButton text={e.example.tp} />
                                <span className="en">{e.example.en}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </React.Fragment>
            ))}
            <div className="footnote">
              <Dots />
              <br />
              {Object.keys(WORDS).length} words · {PARTICLES.length} particles
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- render: a single game ----------------------------------------------
  if (screen === "game") {
    const backToGames = () => {
      setActiveGame(null);
      setScreen("games");
    };
    let gameBody = null;
    if (activeGame === "speed") {
      gameBody = (
        <SpeedQuiz words={unlockedVocab} onExit={backToGames} onAward={awardXp} />
      );
    } else if (activeGame === "memory") {
      gameBody = (
        <MemoryMatch words={unlockedVocab} onExit={backToGames} onAward={awardXp} />
      );
    } else if (activeGame === "builder") {
      gameBody = (
        <SentenceBuilder
          phrases={gamePhrases}
          onExit={backToGames}
          onAward={awardXp}
        />
      );
    } else if (activeGame === "flash") {
      gameBody = <Flashcards words={unlockedVocab} onExit={backToGames} />;
    }
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="subbar">
            <button className="iconbtn" onClick={backToGames} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">
              {GAMES.find((g) => g.key === activeGame)?.sub || "game"}
            </div>
            <div className="stat xp">
              <Icon name="bolt" />
              {progress.xp}
            </div>
          </div>
          {gameBody}
        </div>
      </div>
    );
  }

  // ---- render: teach (intro cards before the quiz) -------------------------
  if (screen === "teach" && lesson) {
    // The carousel is the lesson's new words, then any grammar patterns this
    // lesson introduces.
    const cards = [
      ...lesson.focus.map((w) => ({ kind: "word", w })),
      ...(lesson.grammar || []).map((p) => ({ kind: "grammar", p })),
    ];
    const card = cards[teachIdx];
    const last = teachIdx === cards.length - 1;
    return (
      <div className="tp-root">
        <div className="tp-app">
          <div className="lessonbar">
            <button className="close" onClick={goHome}>
              ×
            </button>
            <div className="progress">
              <i style={{ width: `${((teachIdx + 1) / cards.length) * 100}%` }} />
            </div>
            <button
              className="iconbtn learned-toggle"
              onClick={() => setPanelOpen(true)}
              aria-label="what you've learned so far"
            >
              <Icon name="book" />
            </button>
            <button className="skip" onClick={beginQuiz}>
              skip
            </button>
          </div>
          <LearnedPanel
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            lesson={lesson}
          />
          {card.kind === "word" ? (
            (() => {
              const word = WORDS[card.w];
              const ex = findExample(card.w, lesson.id);
              return (
                <div className="qarea teach">
                  <div className="qkind">
                    new word · {teachIdx + 1} of {cards.length}
                  </div>
                  <div className="teach-word">
                    {card.w}
                    <SpeakButton text={card.w} className="big" />
                  </div>
                  <div className="teach-gloss">{word.g}</div>
                  <Html className="notes" html={word.n} />
                  {ex && (
                    <div className="teach-ex">
                      <div className="teach-ex-tp">
                        {ex.tp}
                        <SpeakButton text={ex.tp} />
                      </div>
                      <div className="teach-ex-en">“{ex.en}”</div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="qarea teach grammar">
              <div className="qkind">
                new grammar · {teachIdx + 1} of {cards.length}
              </div>
              <div className="teach-pat">{card.p.title}</div>
              <Html className="notes" html={card.p.rule} />
              <div className="teach-pat-ex">
                {card.p.examples.map((ex, i) => (
                  <div className="teach-ex" key={i}>
                    <div className="teach-ex-tp">
                      {ex.tp}
                      <SpeakButton text={ex.tp} />
                    </div>
                    <div className="teach-ex-en">“{ex.en}”</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="foot">
            <button
              className="btn"
              onClick={last ? beginQuiz : () => setTeachIdx(teachIdx + 1)}
            >
              {last ? "o kama sona — start practice" : "next ✷"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- render: done --------------------------------------------------------
  if (screen === "done" && doneInfo) {
    return (
      <div className="tp-root">
        <div className="tp-app">
          {doneInfo.passed ? (
            <div className="done-screen">
              <div className="trophy">
                <Icon name="trophy" />
              </div>
              <div className="big">
                {doneInfo.review
                  ? "wawa sin!"
                  : doneInfo.stretch
                  ? "nimi li ken mute!"
                  : "o pini pona!"}
              </div>
              <div className="sub">
                {doneInfo.review
                  ? "warm-up done — a quick mix, nicely handled"
                  : doneInfo.stretch
                  ? "word stretch complete — those words can do more now"
                  : "lesson complete — nicely done"}
              </div>
              <div className="scorecards">
                <div className="scorecard sc-xp">
                  <div className="k">XP earned</div>
                  <div className="v">+{doneInfo.award}</div>
                </div>
                <div className="scorecard sc-acc">
                  <div className="k">accuracy</div>
                  <div className="v">{doneInfo.acc}%</div>
                </div>
                <div className="scorecard">
                  <div className="k">hearts</div>
                  <div className="v" style={{ color: "var(--loje)" }}>
                    {doneInfo.hearts}
                  </div>
                </div>
              </div>
              <button
                className="btn"
                style={{ maxWidth: 320 }}
                onClick={goHome}
              >
                Continue
              </button>
            </div>
          ) : (
            <div className="done-screen">
              <div className="fail-mark" style={{ color: "var(--loje)" }}>
                <Icon name="heartbreak" />
              </div>
              <div className="big">pakala a!</div>
              <div className="sub">
                {doneInfo.review
                  ? "out of hearts — no worries, it was just a warm-up. o kama sin!"
                  : "out of hearts — but mistakes are how you learn. o kama sin!"}
              </div>
              <div className="scorecards">
                <div className="scorecard sc-acc">
                  <div className="k">got right</div>
                  <div className="v">
                    {doneInfo.correct}/{doneInfo.total}
                  </div>
                </div>
              </div>
              <button
                className="btn loje"
                style={{ maxWidth: 320, marginBottom: 12 }}
                onClick={() =>
                  doneInfo.review
                    ? startReview()
                    : doneInfo.stretch
                    ? startStretch(lesson.unit)
                    : startLesson(lesson.unit, lesson.idx)
                }
              >
                Try again
              </button>
              <button
                className="btn ghost"
                style={{ maxWidth: 320 }}
                onClick={goHome}
              >
                Back to path
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- render: lesson ------------------------------------------------------
  const q = lesson.questions[qi];
  const pct = (qi / lesson.questions.length) * 100;

  const heartsMini = (
    <div className="hearts-mini">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} style={{ opacity: i < hearts ? 1 : 0.25 }}>
          <Icon name="heart" />
        </span>
      ))}
    </div>
  );

  const lessonBar = (
    <div className="lessonbar">
      <button className="close" onClick={goHome}>
        ×
      </button>
      <div className="progress">
        <i style={{ width: `${pct}%` }} />
      </div>
      {heartsMini}
      <button
        className="iconbtn learned-toggle"
        onClick={() => setPanelOpen(true)}
        aria-label="what you've learned so far"
      >
        <Icon name="book" />
      </button>
    </div>
  );

  const learnedPanel = (
    <LearnedPanel
      open={panelOpen}
      onClose={() => setPanelOpen(false)}
      lesson={lesson}
    />
  );

  // Matching question has its own layout.
  if (q.type === "match") {
    const ms = ensureMatch(q);
    const allDone = ms.done.length === ms.pairs.length;
    const rows = [];
    for (let i = 0; i < ms.left.length; i++) {
      const tp = ms.left[i];
      const en = ms.right[i];
      const lk = `L:${tp}`;
      const rk = `R:${en}`;
      rows.push(
        <React.Fragment key={i}>
          <button
            className={`tile tp ${ms.done.includes(tp) ? "gone" : ""} ${
              ms.sel === lk ? "sel" : ""
            } ${ms.miss.includes(lk) ? "miss" : ""}`}
            onClick={() => matchTap(lk)}
          >
            {tp}
          </button>
          <button
            className={`tile ${
              ms.done.includes(ms.pairs.find((p) => p.en === en).tp) ? "gone" : ""
            } ${ms.sel === rk ? "sel" : ""} ${ms.miss.includes(rk) ? "miss" : ""}`}
            onClick={() => matchTap(rk)}
          >
            {en}
          </button>
        </React.Fragment>
      );
    }
    return (
      <div className="tp-root">
        <div className="tp-app">
          {lessonBar}
          {learnedPanel}
          <div className="qarea">
            <div className="qkind">tap the matching pairs</div>
            <div className="match">{rows}</div>
          </div>
          <div className="foot">
            <button className="btn" disabled={!allDone} onClick={finishMatch}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Choice-based questions (meaning / word / phrase).
  const renderChoice = (label) => {
    const isCorrect = label === q.correct;
    let cls = "choice";
    if (answered) {
      if (isCorrect) cls += " right";
      else if (label === selection) cls += " wrong";
      else cls += " dim";
    } else if (selection === label) {
      cls += " sel";
    }
    return (
      <button
        key={label}
        className={cls}
        style={answered ? { pointerEvents: "none" } : undefined}
        onClick={() => !answered && setSelection(label)}
      >
        {q.type === "word" ||
        q.type === "transform" ||
        q.type === "listenPhrase" ? (
          <span className="tp">{label}</span>
        ) : (
          <span>{label}</span>
        )}
      </button>
    );
  };

  // Escape hatch for moments when the learner can't listen: flip the
  // current question into its visual twin. A listen question already IS a
  // meaning question underneath; a listenPhrase becomes en → pick-the-tp.
  const showInstead = () => {
    setSelection(null);
    setLesson((prev) => {
      const questions = [...prev.questions];
      const cur = questions[qi];
      questions[qi] =
        cur.type === "listen"
          ? { ...cur, type: "meaning" }
          : { ...cur, noAudio: true };
      return { ...prev, questions };
    });
  };

  let body;
  if (q.type === "listenPhrase" && q.noAudio) {
    body = (
      <>
        <div className="qkind">choose the toki pona</div>
        <div className="qprompt">“{q.en}”</div>
        <div className="choices">{q.options.map(renderChoice)}</div>
      </>
    );
  } else if (q.type === "listen" || q.type === "listenPhrase") {
    const spoken = q.type === "listen" ? q.word : q.tp;
    body = (
      <>
        <div className="qkind">what do you hear?</div>
        <button
          type="button"
          className="listen-play"
          onClick={() => speakTokiPona(spoken)}
          aria-label="play it again"
        >
          <Icon name="speaker" />
          <span>hear it again</span>
        </button>
        <div className="choices">{q.options.map(renderChoice)}</div>
        <button type="button" className="listen-skip" onClick={showInstead}>
          can’t listen right now? show it instead
        </button>
      </>
    );
  } else if (q.type === "meaning") {
    body = (
      <>
        <div className="qkind">what does this mean?</div>
        <div className="qword">
          <TapWord word={q.word} onTap={openTapWord}>
            {q.word}
          </TapWord>
          <SpeakButton text={q.word} className="big" />
        </div>
        <div className="choices">{q.options.map(renderChoice)}</div>
      </>
    );
  } else if (q.type === "word") {
    body = (
      <>
        <div className="qkind">choose the toki pona word</div>
        <div className="qprompt">“{WORDS[q.correct].g}”</div>
        <div className="choices">{q.options.map(renderChoice)}</div>
      </>
    );
  } else if (q.type === "phrase") {
    const tokens = q.tp.split(" ");
    body = (
      <>
        <div className="qkind">translate</div>
        <div className="phrase">
          <span className="phrase-words">
            {tokens.map((tok, i) => (
              <React.Fragment key={i}>
                {i > 0 && " "}
                <TapWord word={wordKey(tok)} onTap={openTapWord}>
                  {tok}
                </TapWord>
              </React.Fragment>
            ))}
          </span>
          <SpeakButton text={q.tp} className="big" />
        </div>
        {q.hasParticle && (
          <Html className="notes" html={PARTICLE_NOTE} />
        )}
        <div className="choices">{q.options.map(renderChoice)}</div>
      </>
    );
  } else if (q.type === "fill") {
    body = (
      <>
        <div className="qkind">which particle fits?</div>
        <div className="phrase fill">
          {q.tokens.map((t, i) =>
            i === q.blank ? (
              <span key={i} className="blank">
                {answered ? q.correct : "?"}
              </span>
            ) : (
              <span key={i} className="tok-word">
                <TapWord word={wordKey(t)} onTap={openTapWord}>
                  {t}
                </TapWord>
              </span>
            )
          )}
        </div>
        <div className="qsub">“{q.en}”</div>
        <div className="choices">{q.options.map(renderChoice)}</div>
      </>
    );
  } else if (q.type === "spot") {
    body = (
      <>
        <div className="qkind">is this correct toki pona?</div>
        <div className="phrase">{q.tp}</div>
        <div className="qsub">pona = correct · ike = has a mistake</div>
        <div className="choices spot">{["pona", "ike"].map(renderChoice)}</div>
      </>
    );
  } else if (q.type === "transform") {
    body = (
      <>
        <div className="qkind">transform the sentence</div>
        <div className="phrase">{q.from}</div>
        <div className="qsub">{q.op}</div>
        <div className="choices">{q.options.map(renderChoice)}</div>
      </>
    );
  } else {
    // build-the-sentence
    const bs = ensureBuild(q);
    body = (
      <>
        <div className="qkind">build the sentence</div>
        <div className="build-prompt">“{q.en}”</div>
        <div
          className={`build-answer ${
            answered ? (lastCorrect ? "right" : "wrong") : ""
          }`}
        >
          {bs.answer.length === 0 && (
            <span className="build-hint">tap words below…</span>
          )}
          {bs.answer.map((item) => (
            <button
              key={item.id}
              className="tok"
              onClick={() => buildToBank(item)}
            >
              {item.tok}
            </button>
          ))}
        </div>
        <div className="build-bank">
          {bs.bank.map((item) => (
            <button
              key={item.id}
              className="tok"
              onClick={() => buildToAnswer(item)}
            >
              {item.tok}
            </button>
          ))}
        </div>
      </>
    );
  }

  // For build, "Check" grades the assembled order; others grade the selection.
  const buildAssembled = buildState
    ? buildState.answer.map((x) => x.tok).join(" ")
    : "";
  const canCheck =
    q.type === "build" ? !!(buildState && buildState.answer.length) : !!selection;
  const doCheck = () =>
    grade(
      q.type === "build"
        ? buildAssembled === q.correct ||
            (q.alts || []).includes(buildAssembled)
        : selection === q.correct
    );

  return (
    <div className="tp-root">
      <div className="tp-app" ref={appRef}>
        {lessonBar}
        {learnedPanel}
        <div className="qarea">{body}</div>
        {!answered ? (
          <div className="foot">
            <button className="btn" disabled={!canCheck} onClick={doCheck}>
              Check
            </button>
          </div>
        ) : (
          <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
            <div className="verdict">
              <b>{lastCorrect ? "pona!" : "ike a"}</b>
              <small>
                {lastCorrect ? "that's right" : `answer: ${q.correct}`}
              </small>
            </div>
            {(q.type === "meaning" || q.type === "word" || q.type === "listen") &&
            q.word ? (
              <Html
                className="notes"
                html={
                  q.type === "listen"
                    ? `you heard <b>${q.word}</b>. ${WORDS[q.word].n}`
                    : WORDS[q.word].n
                }
              />
            ) : q.type === "listenPhrase" ? (
              <Html
                className="notes"
                html={
                  q.noAudio
                    ? `<b>${q.tp}</b> &nbsp;→&nbsp; “${q.en}”.`
                    : `you heard <b>${q.tp}</b> &nbsp;→&nbsp; “${q.en}”.`
                }
              />
            ) : q.type === "phrase" ? (
              <Html
                className="notes"
                html={
                  `<b>${q.tp}</b> &nbsp;→&nbsp; ${q.correct}.` +
                  (q.note ? ` ${q.note}` : "")
                }
              />
            ) : q.type === "fill" ? (
              <Html
                className="notes"
                html={`<b>${q.tokens.join(" ")}</b> &nbsp;→&nbsp; ${q.en}.`}
              />
            ) : q.type === "spot" ? (
              <Html
                className="notes"
                html={`<b>${q.ok ? "pona" : "ike"}.</b> ${q.why}`}
              />
            ) : q.type === "build" ? (
              <Html
                className="notes"
                html={
                  (lastCorrect && buildAssembled !== q.tp
                    ? `<b>${buildAssembled}</b> &nbsp;→&nbsp; ${q.en}. (<b>${q.tp}</b> works too — both orders are good toki pona.)`
                    : `<b>${q.tp}</b> &nbsp;→&nbsp; ${q.en}.`) +
                  (q.note ? ` ${q.note}` : "")
                }
              />
            ) : q.type === "transform" ? (
              <Html
                className="notes"
                html={`<b>${q.from}</b> &nbsp;→&nbsp; <b>${q.correct}</b>`}
              />
            ) : null}
            <button
              className={`btn ${lastCorrect ? "" : "loje"}`}
              onClick={advance}
            >
              Continue
            </button>
          </div>
        )}
        <WordPopover tap={tapPopover} onClose={() => setTapPopover(null)} />
      </div>
    </div>
  );
};

// Slide-over "learned so far" panel — new-lesson words (tap to expand the
// full note), grammar patterns introduced this lesson, and a collapsed
// "known so far" chip list (tap a chip to reveal its gloss).
const LearnedPanel = ({ open, onClose, lesson }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [revealed, setRevealed] = useState(() => new Set());
  const [knownOpen, setKnownOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!lesson) return null;

  const toggle = (setFn, key) =>
    setFn((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // A stretch quiz's own id (`u3-x`) isn't in the curriculum order, so its
  // "known so far" is everything through the unit's last real lesson.
  const knownId = lesson.stretch
    ? `${lesson.unit.id}-${lesson.unit.lessons - 1}`
    : lesson.id;
  const known = [...wordsThroughLesson(knownId)].filter(
    (w) => !lesson.focus.includes(w)
  );

  return (
    <>
      <div
        className={`learned-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />
      <div className={`learned-panel ${open ? "open" : ""}`}>
        <div className="learned-head">
          <div className="learned-title">learned so far</div>
          <button className="learned-close" onClick={onClose} aria-label="close">
            ×
          </button>
        </div>
        <div className="learned-body">
          <div className="learned-section">
            <div className="learned-label">
              {lesson.stretch ? "words being stretched" : "new this lesson"}
            </div>
            {lesson.focus.map((w) => (
              <div className="learned-word" key={w}>
                <button
                  className="learned-word-row"
                  onClick={() => toggle(setExpanded, w)}
                >
                  <span className="tp">{w}</span>
                  <span className="g">{WORDS[w].g}</span>
                </button>
                {expanded.has(w) && (
                  <Html className="notes" html={WORDS[w].n} />
                )}
              </div>
            ))}
          </div>

          {lesson.grammar && lesson.grammar.length > 0 && (
            <div className="learned-section">
              <div className="learned-label">grammar in this lesson</div>
              {lesson.grammar.map((p) => (
                <div className="learned-grammar" key={p.id}>
                  <div className="learned-grammar-title">{p.title}</div>
                  <Html className="notes" html={p.rule} />
                  {p.examples.map((ex, i) => (
                    <div className="learned-ex" key={i}>
                      <span className="tp">{ex.tp}</span>
                      <span className="en">“{ex.en}”</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="learned-section">
            <button
              className="learned-known-toggle"
              onClick={() => setKnownOpen((v) => !v)}
            >
              <span className="chev">{knownOpen ? "▾" : "▸"}</span>
              known so far ({known.length})
            </button>
            {knownOpen && (
              <div className="learned-chips">
                {known.map((w) => (
                  <button
                    key={w}
                    className="learned-chip"
                    onClick={() => toggle(setRevealed, w)}
                  >
                    {revealed.has(w) ? WORDS[w].g : w}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Small inline "what should we call you?" control on the home hero.
const NameBar = ({ name, onSave }) => {
  const [value, setValue] = useState(name || "");
  useEffect(() => {
    setValue(name || "");
  }, [name]);
  const dirty = value.trim() && value.trim() !== (name || "");
  return (
    <div className="namebar">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty) onSave(value.trim());
        }}
        placeholder="what should we call you?"
      />
      <button disabled={!dirty} onClick={() => onSave(value.trim())}>
        save
      </button>
    </div>
  );
};

export default TokiPona;
